import {
  ASSISTANT_PROMPT_MAX,
  ASSISTANT_RATING_REASON_LABELS,
  assistantDecisionBar,
  assistantExamplePrompts,
  assistantPickIsStale,
  assistantProposalView,
  assistantRatingReasons,
  type AssistantConversation,
  type AssistantRating,
  type AssistantRatingReason,
  type AssistantTurn,
  type MyBusiness,
} from "@teamna/shared";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { copyForError } from "@teamna/web-client/api/copy.ts";
import { track } from "@teamna/web-client/analytics/web.ts";
import {
  useAssistantConversation,
  useAssistantMore,
  useAssistantTurn,
  useConfirmAssistantAction,
  useEndAssistantChat,
  useRateAssistantTurn,
} from "@teamna/web-client/hooks/use-assistant.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { formatRelativeDay } from "@teamna/web-client/lib/format.ts";
import { Button, ComposerField, ErrorState, InlineError, SkeletonList, TextAreaField, ThumbDownIcon, ThumbUpIcon } from "../../components/index.ts";
import {
  AssistantFacts,
  AssistantProposal,
  AssistantResult,
  AssistantShare,
  AssistantTable,
  AssistantText,
  AssistantToolCalls,
  AssistantWorking,
  ChatStatusPill,
  UserBubble,
} from "./assistant-blocks.tsx";
import { VoiceButton } from "./voice.tsx";

/**
 * One AI Assistant chat (strategy repo `teamna/strategy/ai-assistant-v1.md`, section 8, prototype
 * "Talk It Through"). The same chat shows in the side sheet on Home and on the AI Assistant page: the
 * head (status, where it came from, Mark done), the turns, and at the bottom the decision bar (the
 * plan on the table and Confirm, Not now, Close), the next questions, and the composer.
 *
 * A plan changes nothing until Confirm. Several options of one choice show "Pick this", and the
 * decision bar confirms the picked one. A newer plan replaces the older one; the older one stays in
 * the chat, marked "Replaced".
 */

export function AssistantChat({
  business,
  conversationId,
  variant,
  onCreated,
  headExtra,
}: {
  business: MyBusiness;
  /** Null: a new question (the AI Assistant page only). */
  conversationId: string | null;
  /** `page` shows the title in the head; in the `sheet`, the panel header shows it. */
  variant: "page" | "sheet";
  /** The first question made a conversation. */
  onCreated?: (conversationId: string) => void;
  /** More in the head, for example "Open in AI Assistant" in the sheet. */
  headExtra?: ReactNode;
}) {
  const { label } = useLabels();
  const conversation = useAssistantConversation(business.businessId, conversationId);
  const ask = useAssistantTurn(business.businessId);
  const [draft, setDraft] = useState("");
  // The text in the box came from the microphone: the question is sent as a voice question.
  const [fromVoice, setFromVoice] = useState(false);
  // The box as it is now, for the error callback of a send that started earlier.
  const draftNow = useRef(draft);
  useEffect(() => {
    draftNow.current = draft;
  }, [draft]);
  const [picked, setPicked] = useState<string | null>(null);

  const data = conversation.data;
  const turns: AssistantTurn[] = data?.turns ?? [];
  const pending = ask.isPending ? ask.variables : undefined;
  // The end of the list scrolls into view on a new turn and on "working": its key changes.
  const end = `${turns.at(-1)?.id ?? "none"}-${pending ? "pending" : "idle"}`;

  /**
   * A question from the composer leaves the box at once: the chat shows it as sent while the answer
   * comes (pilot, 2026-10-23). If the send fails, the text comes back, unless the person typed again.
   * A chip sends its own words and leaves the box as it is.
   */
  function send(text: string, fromComposer = false) {
    const voice = fromComposer && fromVoice;
    if (fromComposer) {
      setDraft("");
      setFromVoice(false);
    }
    ask.mutate(
      { ...(conversationId ? { conversationId } : {}), text, inputMode: voice ? "voice" : "text" },
      {
        onSuccess: (result) => {
          if (!conversationId) onCreated?.(result.conversation.id);
        },
        onError: () => {
          if (!fromComposer) return;
          const restored = restoreFailedSend(draftNow.current, text, voice);
          if (!restored) return;
          setDraft(restored.draft);
          setFromVoice(restored.fromVoice);
        },
      },
    );
  }

  /** The words of a recording go after what the person typed already. */
  function addVoiceText(text: string) {
    setDraft((current) => (current.trim() ? `${current.trim()} ${text}` : text));
    setFromVoice(true);
  }

  let body;
  if (conversationId && conversation.isLoading) {
    body = <SkeletonList rows={3} />;
  } else if (conversationId && conversation.error) {
    body = <ErrorState error={conversation.error} onRetry={() => void conversation.refetch()} />;
  } else if (turns.length === 0 && !pending) {
    body = (
      <div className="py-6">
        <p className="text-[15px] font-semibold">Ask a question about {business.name}.</p>
        <p className="pb-3 text-[14px] text-ink-2">For example:</p>
        <div className="flex flex-wrap gap-2">
          {assistantExamplePrompts(label("participant", { plural: true, capital: true })).map((text) => (
            <ChipButton key={text} disabled={ask.isPending} onClick={() => send(text)}>
              {text}
            </ChipButton>
          ))}
        </div>
      </div>
    );
  } else {
    const lastAnswerId = turns.findLast((turn) => turn.role === "assistant")?.id ?? null;
    body = (
      <ol className="space-y-4 py-4">
        {turns.map((turn) => (
          <li key={turn.id}>
            <TurnView
              turn={turn}
              conversation={data!}
              businessId={business.businessId}
              timezone={business.timezone}
              picked={picked}
              onPick={setPicked}
              last={turn.id === lastAnswerId}
            />
          </li>
        ))}
        {pending ? (
          <>
            <li>
              <UserBubble text={pending.text} />
            </li>
            <li>
              <AssistantWorking />
            </li>
          </>
        ) : null}
      </ol>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {data ? <ChatHead conversation={data} business={business} variant={variant} extra={headExtra} /> : null}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5">
        {body}
        <ScrollIntoView key={end} />
      </div>
      <div className="space-y-2.5 border-t border-line bg-paper px-5 py-3">
        {data ? <DecisionBar business={business} conversation={data} picked={picked} onPick={setPicked} /> : null}
        {data && data.chips.length > 0 ? (
          <div className="flex gap-2 overflow-x-auto pb-0.5">
            {data.chips.map((chip) => (
              <ChipButton key={chip} disabled={ask.isPending} onClick={() => send(chip)} nowrap>
                {chip}
              </ChipButton>
            ))}
          </div>
        ) : null}
        <ComposerField
          id={`assistant-composer-${variant}`}
          label="Your question"
          value={draft}
          onChange={(value) => {
            setDraft(value);
            if (!value.trim()) setFromVoice(false);
          }}
          onSend={(text) => send(text, true)}
          placeholder={data?.cardKind ? "Ask, or say what to change" : "Ask AI Assistant"}
          max={ASSISTANT_PROMPT_MAX}
          pending={ask.isPending}
          error={ask.error ? copyForError(ask.error).message : null}
          before={<VoiceButton businessId={business.businessId} disabled={ask.isPending} onText={addVoiceText} />}
        />
        <p className="text-[12px] text-ink-2">AI Assistant reads your data. It changes nothing until you tap Confirm.</p>
      </div>
    </div>
  );
}

/**
 * After a failed send from the composer: the question comes back into an empty box, with its voice
 * mark. If the person typed again meanwhile, the box keeps the new text and its own mark (review F1.3).
 */
export function restoreFailedSend(current: string, sent: string, sentByVoice: boolean): { draft: string; fromVoice: boolean } | null {
  return current === "" ? { draft: sent, fromVoice: sentByVoice } : null;
}

function ChipButton({ children, onClick, disabled, nowrap = false }: { children: ReactNode; onClick: () => void; disabled?: boolean; nowrap?: boolean }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        "rounded-full border border-line bg-surface px-3.5 py-1.5 text-left text-[13.5px] text-ink hover:border-accent-soft disabled:opacity-50",
        nowrap ? "shrink-0 whitespace-nowrap" : "",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Head
// ---------------------------------------------------------------------------

function ChatHead({ conversation, business, variant, extra }: { conversation: AssistantConversation; business: MyBusiness; variant: "page" | "sheet"; extra?: ReactNode }) {
  const end = useEndAssistantChat(business.businessId);
  const finished = conversation.status === "done" || conversation.status === "closed";
  const source = conversation.cardKind ? "Suggestion by AI Assistant" : "You asked";
  return (
    <div className="border-b border-line px-5 py-3">
      {variant === "page" ? (
        <h2 dir="auto" className="pb-1 text-[18px]">
          {conversation.title}
        </h2>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <ChatStatusPill status={conversation.status} />
        <span className="text-[13px] text-ink-2">
          {source}, {formatRelativeDay(conversation.createdAt)}
        </span>
        <span className="ml-auto flex items-center gap-2">
          {extra}
          {finished ? null : (
            <Button size="sm" variant="secondary" loading={end.isPending} onClick={() => end.mutate({ conversationId: conversation.id, kind: "done" })}>
              Mark done
            </Button>
          )}
        </span>
      </div>
      {end.error ? <InlineError error={end.error} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Decision bar
// ---------------------------------------------------------------------------

function DecisionBar({
  business,
  conversation,
  picked,
  onPick,
}: {
  business: MyBusiness;
  conversation: AssistantConversation;
  picked: string | null;
  onPick: (actionId: string | null) => void;
}) {
  const confirm = useConfirmAssistantAction(business.businessId);
  const end = useEndAssistantChat(business.businessId);
  const [closing, setClosing] = useState(false);
  if (assistantPickIsStale(conversation, picked)) onPick(null);
  const bar = assistantDecisionBar(conversation, picked);
  if (!bar) return null;
  const { chosen } = bar;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {bar.confirmLabel === null ? null : (
          <Button
            size="md"
            variant="primary"
            disabled={!chosen}
            loading={confirm.isPending}
            onClick={() => chosen && confirm.mutate(chosen.id, { onSuccess: () => onPick(null) })}
          >
            {bar.confirmLabel}
          </Button>
        )}
        {bar.canEnd ? (
          <>
            <Button size="md" variant="secondary" loading={end.isPending && end.variables?.kind === "not-now"} onClick={() => end.mutate({ conversationId: conversation.id, kind: "not-now" })}>
              Not now
            </Button>
            <Button size="md" variant="ghost" aria-expanded={closing} onClick={() => setClosing((value) => !value)}>
              Close
            </Button>
          </>
        ) : null}
      </div>
      {closing && conversation.closeReasons.length > 0 ? (
        <fieldset className="m-0 flex min-w-0 flex-wrap items-center gap-2 border-0 p-0">
          <legend className="float-left pe-1 text-[13px] font-semibold text-ink-2">Why close it?</legend>
          {conversation.closeReasons.map((reason) => (
            <ChipButton
              key={reason.key}
              disabled={end.isPending}
              onClick={() => end.mutate({ conversationId: conversation.id, kind: "close", reason: reason.key }, { onSuccess: () => setClosing(false) })}
            >
              {reason.label}
            </ChipButton>
          ))}
        </fieldset>
      ) : null}
      {confirm.error ? <InlineError error={confirm.error} /> : null}
      {end.error ? <InlineError error={end.error} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Turns
// ---------------------------------------------------------------------------

/** Scrolls itself into view once, when it mounts. A new `key` mounts it again. */
function ScrollIntoView() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView({ block: "end" });
  }, []);
  return <div ref={ref} />;
}

function TurnView({
  turn,
  conversation,
  businessId,
  timezone,
  picked,
  onPick,
  last,
}: {
  turn: AssistantTurn;
  conversation: AssistantConversation;
  businessId: string;
  timezone: string;
  picked: string | null;
  onPick: (actionId: string | null) => void;
  /** The newest answer of the chat: only its tables offer More, so one tap gives one answer. */
  last: boolean;
}) {
  const more = useAssistantMore(businessId);
  if (turn.role === "user") return <UserBubble text={turn.text} />;
  if (turn.role === "event") {
    return <p className="text-center text-[12.5px] text-ink-2">{turn.text}</p>;
  }
  return (
    <div>
      {turn.text ? <AssistantText text={turn.text} error={turn.outcome === "error"} /> : null}
      {turn.blocks.map((block, index) => {
        // A chart draws in the card of the table right after it.
        if (block.type === "chart") return null;
        if (block.type === "table") {
          const before = turn.blocks[index - 1];
          return (
            <AssistantTable
              key={`${block.title}|${block.caption ?? ""}`}
              block={block}
              chart={before?.type === "chart" ? before : undefined}
              timezone={timezone}
              onMore={last ? () => more.mutate({ turnId: turn.id, block: index }) : undefined}
              moreBusy={more.isPending}
            />
          );
        }
        if (block.type === "facts") return <AssistantFacts key={block.title} block={block} />;
        if (block.type === "result") return <AssistantResult key={block.text} block={block} />;
        if (block.type === "share") {
          return (
            <AssistantShare
              key={block.title}
              block={block}
              onCopied={() => track("open_spots_shared", { spots_count: block.spots, sessions_count: block.sessions, channel: "copy", from: "assistant" })}
            />
          );
        }
        if (block.type === "proposal") {
          return (
            <AssistantProposal
              key={block.actionId}
              block={block}
              view={assistantProposalView(conversation, block)}
              picked={picked === block.actionId}
              onPick={() => onPick(picked === block.actionId ? null : block.actionId)}
            />
          );
        }
        // A block type from a newer server: nothing, never a broken card.
        return null;
      })}
      {more.error ? <InlineError error={more.error} /> : null}
      <AssistantToolCalls calls={turn.toolCalls} />
      {turn.rateable ? <AnswerRating businessId={businessId} conversationId={conversation.id} turn={turn} /> : null}
    </div>
  );
}

const RATING_NOTE_MAX = 500;

/**
 * Thumbs up or down on one answer, so Teamna learns what works. A thumbs down asks what was wrong
 * (a fixed list) and takes a short note that only Teamna reads. A second tap on the same thumb takes
 * the rating back.
 */
function AnswerRating({ businessId, conversationId, turn }: { businessId: string; conversationId: string; turn: AssistantTurn }) {
  const rate = useRateAssistantTurn(businessId);
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState<AssistantRatingReason | null>(null);
  const [note, setNote] = useState("");
  const current = turn.rating ?? null;
  // The first thumbs down saves at once and keeps the form open for the reason; Send closes it.
  const send = (rating: AssistantRating | null, more: { reason: AssistantRatingReason | null; note: string | null } = { reason: null, note: null }, close = true) =>
    rate.mutate({ conversationId, turnId: turn.id, rating, ...more }, { onSuccess: () => (close ? setAsking(false) : undefined) });

  const thumb = (rating: AssistantRating) => {
    if (current === rating) return send(null);
    send(rating, undefined, rating !== "down");
    if (rating === "down") {
      setReason(null);
      setNote("");
      setAsking(true);
    } else setAsking(false);
  };

  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center gap-1">
        {(["up", "down"] as const).map((rating) => {
          const Icon = rating === "up" ? ThumbUpIcon : ThumbDownIcon;
          const on = current === rating;
          return (
            <button
              key={rating}
              type="button"
              aria-pressed={on}
              aria-label={rating === "up" ? "Good answer" : "Not a good answer"}
              disabled={rate.isPending}
              onClick={() => thumb(rating)}
              className={[
                "flex size-8 items-center justify-center rounded-full transition-colors disabled:opacity-50",
                on ? "bg-accent-surface text-accent-strong" : "text-ink-3 hover:bg-surface hover:text-ink",
              ].join(" ")}
            >
              <Icon size={17} />
            </button>
          );
        })}
        {current && !asking ? <span className="ps-1 text-[12.5px] text-ink-3">Thank you. Teamna reads this to make AI Assistant better.</span> : null}
      </div>
      {asking ? (
        <form
          className="space-y-2 rounded-card border border-line bg-surface p-3"
          onSubmit={(event) => {
            event.preventDefault();
            send("down", { reason, note: note.trim() || null });
          }}
        >
          <fieldset className="m-0 flex min-w-0 flex-wrap items-center gap-2 border-0 p-0">
            <legend className="float-left pe-1 text-[13px] font-semibold text-ink-2">What was wrong?</legend>
            {assistantRatingReasons.map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed={reason === key}
                onClick={() => setReason(reason === key ? null : key)}
                className={[
                  "rounded-full border px-3 py-1 text-[13px]",
                  reason === key ? "border-accent bg-accent-surface text-accent-strong" : "border-line bg-paper text-ink hover:border-accent-soft",
                ].join(" ")}
              >
                {ASSISTANT_RATING_REASON_LABELS[key]}
              </button>
            ))}
          </fieldset>
          <TextAreaField
            label="Tell us more (optional)"
            hint="Only the Teamna team reads this."
            rows={2}
            maxLength={RATING_NOTE_MAX}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <div className="flex gap-2">
            <Button size="sm" type="submit" loading={rate.isPending}>
              Send
            </Button>
            <Button size="sm" variant="ghost" type="button" onClick={() => setAsking(false)}>
              Skip
            </Button>
          </div>
        </form>
      ) : null}
      {rate.error ? <InlineError error={rate.error} /> : null}
    </div>
  );
}
